import { useMemo } from 'react';
import { Box, CircularProgress, FormHelperText, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOnboardingReassignCandidates } from '../../../hooks/useOnboarding';
import { OnboardingFormSelect } from '../OnboardingFormSelect';
import { useOnboardingLens } from '../onboardingLens';

interface OnboardingStepReassignProps {
  orgId?: string;
  currentUserId?: string;
  selectedUserId: string;
  onSelectedUserIdChange: (userId: string) => void;
  showErrors: boolean;
  submitError?: string | null;
}

export function OnboardingStepReassign({
  orgId,
  currentUserId,
  selectedUserId,
  onSelectedUserIdChange,
  showErrors,
  submitError,
}: OnboardingStepReassignProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { layout, typography, typeDefault, typeMuted } = useOnboardingLens();
  const { reassign } = layout;

  const {
    data: usersData,
    isLoading,
    isError,
  } = useOnboardingReassignCandidates(orgId, Boolean(orgId));

  const colleagueOptions = useMemo(() => {
    const users = usersData ?? [];
    return users
      .filter((user) => user.id !== currentUserId)
      .map((user) => ({
        value: user.id,
        label: user.email,
      }));
  }, [currentUserId, usersData]);

  const fieldError =
    showErrors && !selectedUserId
      ? t('onboarding.wizard.reassign.errors.required')
      : undefined;

  const renderColleagueField = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: layout.formField.labelToInputGap,
            minHeight: layout.formField.inputHeight,
            width: reassign.leftColumnContentWidth,
          }}
          aria-live="polite"
        >
          <CircularProgress size={20} aria-hidden />
          <Typography sx={{ ...typography.textSm, color: typeMuted }}>
            {t('onboarding.wizard.reassign.loading_users')}
          </Typography>
        </Box>
      );
    }

    if (isError) {
      return (
        <FormHelperText error sx={{ mx: 0, width: reassign.leftColumnContentWidth }}>
          {t('onboarding.wizard.reassign.load_users_error')}
        </FormHelperText>
      );
    }

    if (colleagueOptions.length === 0) {
      return (
        <FormHelperText sx={{ mx: 0, color: typeMuted, width: reassign.leftColumnContentWidth }}>
          {t('onboarding.wizard.reassign.no_colleagues')}
        </FormHelperText>
      );
    }

    return (
      <OnboardingFormSelect
        id="onboarding-reassign-colleague"
        label={t('onboarding.wizard.reassign.colleague_label')}
        value={selectedUserId}
        options={colleagueOptions}
        placeholder={t('onboarding.wizard.reassign.colleague_placeholder')}
        error={fieldError}
        required
        onChange={(event) => onSelectedUserIdChange(event.target.value)}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        width: reassign.leftColumnWidth,
        maxWidth: '100%',
        height: '100%',
        px: theme.spacing(4),
        py: theme.spacing(5),
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          width: reassign.leftColumnContentWidth,
          maxWidth: '100%',
        }}
      >
        <Typography component="h2" sx={{ ...typography.h2Display, color: typeDefault }}>
          {t('onboarding.wizard.reassign.title')}
        </Typography>
        <Typography sx={{ ...typography.textSm, color: typeMuted }}>
          {t('onboarding.wizard.reassign.subtitle')}
        </Typography>
      </Box>

      <Box sx={{ width: reassign.leftColumnContentWidth, maxWidth: '100%' }}>
        {renderColleagueField()}
        {submitError ? (
          <FormHelperText error sx={{ mx: 0, mt: layout.formField.labelGap }} role="alert">
            {submitError}
          </FormHelperText>
        ) : null}
      </Box>
    </Box>
  );
}
